import { useTooltip } from "@visx/tooltip";
import { bisector } from "d3-array";
import { compact, isEmpty, map } from "lodash/fp";
import { DateTime } from "luxon";

import {
  SingleTimeSeriesData,
  MultipleTimeSeriesTooltipData,
} from "@app/analytics/MeetingReport/components/MultipleTimeSeriesChart";
import { SpeakerEvent } from "@app/analytics/MeetingReport/types";
import { TimeSeriesData } from "@app/common/types";
import { formatTimeTick } from "@app/common/utils";

import { TOOLTIP_WIDTH } from "./DeepDiveChartTooltip";
import { getNearestDataPoint } from "../../utils/eventUtils";

const INITIAL_TOOLTIP = { tooltipOpen: false, tooltipData: { indicators: [] } };

const bisectTimestampFromX = bisector<TimeSeriesData, number>((d) => d.x).left;

export type ChartMetric = "meetingScore" | "engagement" | "sentiment";

export const COLOR_BY_METRIC = {
  meetingScore: "#4C47CC",
  engagement: "#FF6152",
  sentiment: "#E341E7",
};

const isWithinBounds = (
  event: { rawStartTime: DateTime; rawEndTime: DateTime },
  timestamp: number
) =>
  event &&
  event.rawStartTime.toMillis() <= timestamp &&
  event.rawEndTime.toMillis() >= timestamp;

const getSpeakers = (speakerEvents: SpeakerEvent[], timestamp: number) => {
  if (isEmpty(speakerEvents)) return;

  const intersectingEvents = speakerEvents.filter((event) =>
    isWithinBounds(event, timestamp)
  );

  if (isEmpty(intersectingEvents)) return;

  return {
    id: "speakers",
    value: compact(map("speaker", intersectingEvents)),
  };
};

const useDeepDiveChartTooltip = ({
  domain,
  enabledMetrics,
  series,
  speakerEvents,
}: {
  domain: [number, number];
  enabledMetrics: ChartMetric[];
  series: SingleTimeSeriesData[];
  speakerEvents: SpeakerEvent[];
}) => {
  const tooltip = useTooltip<MultipleTimeSeriesTooltipData>(INITIAL_TOOLTIP);

  const dataByMetric = series.reduce(
    (data, series) => {
      data[series.id as ChartMetric] = series.data;
      return data;
    },
    {} as { [key in ChartMetric]: TimeSeriesData[] }
  );

  const getIndicator = (
    metric: ChartMetric,
    index: number,
    timestamp: number
  ) => {
    if (!enabledMetrics.includes(metric)) return;
    const dataPoint = getNearestDataPoint(
      dataByMetric[metric],
      index,
      timestamp
    );

    if (!dataPoint) return;

    return {
      id: metric,
      rawValue: dataPoint.y,
      value: Math.round(dataPoint.y),
      color: COLOR_BY_METRIC[metric],
    };
  };

  const handleUpdateTooltip = (x: number, timestamp: number) => {
    // engagement should be available for the duration of the meeting for both lite and
    // full meetings, so use that dataset as the default for finding the index
    const index = bisectTimestampFromX(dataByMetric.engagement, timestamp);

    const meetingScoreIndicator = getIndicator(
      "meetingScore",
      index,
      timestamp
    );
    const engagementIndicator = getIndicator("engagement", index, timestamp);
    const sentimentIndicator = getIndicator("sentiment", index, timestamp);

    const indicators = compact([
      meetingScoreIndicator,
      engagementIndicator,
      sentimentIndicator,
    ]);

    const speakers = getSpeakers(speakerEvents, timestamp);

    tooltip.showTooltip({
      tooltipLeft: x,
      tooltipData: {
        timestamp: formatTimeTick(Math.max(0, timestamp - domain[0])),
        indicators,
        additionalData: compact([speakers]),
      },
    });
  };

  return {
    ...tooltip,
    width: TOOLTIP_WIDTH,
    onTooltipUpdate: handleUpdateTooltip,
  };
};

export default useDeepDiveChartTooltip;
